import React, { useState, useEffect } from 'react'
import RangeSlider from '../Sliders/RangeSlider'
import Select from 'react-select'
import { cn } from '@/lib/utils'

type CompletedSystems = {
    variable?: string
    sensors: { _id: string; name: string }[]
    controllers: { _id: string; name: string }[]
}

type SensorGroupProps = {
    system: { sensorGroups?: unknown[]; sensor_groups?: unknown[] }
    completedSystems: CompletedSystems
    program: Record<string, number>
    advancedOptions: Record<string, unknown>
    setAdvancedOptionsState: React.Dispatch<React.SetStateAction<Record<string, unknown>>>
    customStyles: any
    setSensorGroups: React.Dispatch<React.SetStateAction<any[]>>
    sensorGroups: any[]
}

function getLoopImageSrc(variable: string | undefined): string {
    const v = String(variable ?? '').toLowerCase()
    if (v.includes('temp')) return '/images/backgrounds/temperature-background.jpg'
    if (v.includes('humid')) return '/images/backgrounds/humidity-background.jpg'
    if (v.includes('ph')) return '/images/backgrounds/ph-background.png'
    if (v.includes('ec')) return '/images/backgrounds/ec-background.png'
    return '/images/backgrounds/default-background.jpg'
}

const DEFAULT_TOLERANCE: Record<string, [number, number]> = {
    ph: [0.25, 0.25],
    ec: [0.01, 0.01],
    temperature: [2, 2],
    humidity: [5, 5],
    water_flow_rate: [1, 1],
    moisture: [2, 2],
}

function loopGradientClass(variable: string | undefined): string {
    const v = String(variable ?? '').toLowerCase()
    if (v.includes('temp'))
        return 'from-orange-600/30 via-rose-500/15 to-sky-600/25'
    if (v.includes('humid')) return 'from-sky-500/25 via-cyan-500/15 to-indigo-600/25'
    if (v.includes('ph')) return 'from-violet-600/30 via-fuchsia-500/15 to-purple-700/25'
    if (v.includes('ec')) return 'from-amber-500/30 via-yellow-500/15 to-orange-600/25'
    return 'from-emerald-600/25 via-teal-600/15 to-zinc-800/40'
}

const SensorGroup = ({
    system,
    completedSystems,
    program,
    advancedOptions,
    setAdvancedOptionsState,
    customStyles,
    setSensorGroups,
    sensorGroups,
}: SensorGroupProps) => {
    const [assignedSensors, setAssignedSensors] = useState<{ value: string; label: string }[]>([])
    const [assignedControllers, setAssignedControllers] = useState<{ value: string; label: string }[]>([])
    const [, setMappedAssignedSensors] = useState<{ value: string; label: string }[]>([])
    const [, setMappedAssignedControllers] = useState<{ value: string; label: string }[]>([])

    useEffect(() => {
        const groupsOnSystem = (system.sensor_groups ?? system.sensorGroups ?? []) as {
            variable?: string
            sensors?: string[]
            controllers?: string[]
        }[]
        const relevantSensors = completedSystems.sensors.filter((sensor) =>
            groupsOnSystem.some((group) => Array.isArray(group.sensors) && group.sensors.includes(sensor._id))
        )
        const relevantControllers = completedSystems.controllers.filter((controller) =>
            groupsOnSystem.some(
                (group) => Array.isArray(group.controllers) && group.controllers.includes(controller._id)
            )
        )
        const sensorGroup = groupsOnSystem.find((group) => group.variable === completedSystems.variable)
        if (sensorGroup) {
            setAssignedSensors(
                relevantSensors
                    .filter((sensor) => sensorGroup.sensors?.includes(sensor._id))
                    .map((sensor) => ({
                        value: sensor._id,
                        label: sensor.name,
                    }))
            )
            setAssignedControllers(
                relevantControllers
                    .filter((controller) => sensorGroup.controllers?.includes(controller._id))
                    .map((controller) => ({
                        value: controller._id,
                        label: controller.name,
                    }))
            )
        }
    }, [])

    useEffect(() => {
        const relevantSensors = completedSystems.sensors.filter((sensor) =>
            sensorGroups.some((group) => Array.isArray(group?.sensors) && group.sensors.includes(sensor._id))
        )
        const relevantControllers = completedSystems.controllers.filter((controller) =>
            sensorGroups.some(
                (group) => Array.isArray(group?.controllers) && group.controllers.includes(controller._id)
            )
        )
        const sensorGroup = sensorGroups.find((group) => group.variable === completedSystems.variable)
        if (sensorGroup) {
            setAssignedSensors(
                relevantSensors
                    .filter((sensor) => sensorGroup.sensors?.includes(sensor._id))
                    .map((sensor) => ({
                        value: sensor._id,
                        label: sensor.name,
                    }))
            )
            setAssignedControllers(
                relevantControllers
                    .filter((controller) => sensorGroup.controllers?.includes(controller._id))
                    .map((controller) => ({
                        value: controller._id,
                        label: controller.name,
                    }))
            )
        }
    }, [sensorGroups])

    const variable = completedSystems.variable ?? 'Control'
    const tolKey = String(completedSystems.variable).toLowerCase()
    const imageSrc = getLoopImageSrc(completedSystems.variable)
    const gradient = loopGradientClass(completedSystems.variable)
    const tolerances = (advancedOptions as { tolerances?: Record<string, [number, number]> }).tolerances
    const toleranceDefault = tolerances?.[tolKey] ?? DEFAULT_TOLERANCE[tolKey] ?? [0.25, 0.25]

    const sensorOptions = completedSystems.sensors.map((sensor) => ({
        value: sensor._id,
        label: sensor.name,
    }))

    const controllerOptions = completedSystems.controllers.map((controller) => ({
        value: controller._id,
        label: controller.name,
    }))

    const fieldLabel = 'mb-1.5 block text-xs font-medium uppercase tracking-wide text-zinc-500'

    return (
        <div className="control-loop-root flex flex-col gap-5">
            <div className="overflow-hidden rounded-xl border border-zinc-700/90 bg-zinc-950/60 shadow-inner shadow-black/20">
                <div className="relative flex min-h-[9.5rem] flex-col sm:min-h-[8.5rem] sm:flex-row">
                    <div
                        className={cn(
                            'pointer-events-none absolute inset-0 bg-gradient-to-br opacity-90',
                            gradient
                        )}
                        aria-hidden
                    />
                    <div className="relative z-[1] flex flex-1 flex-col justify-center gap-2 p-4 sm:p-5">
                        <span className="inline-flex w-fit items-center rounded-full border border-emerald-500/30 bg-zinc-950/50 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-300/95">
                            {String(variable)}
                        </span>
                        <p className="max-w-md text-sm leading-relaxed text-zinc-300">
                            Map sensors and actuators for this loop. Tolerance band is relative to the program target in
                            automatic mode.
                        </p>
                    </div>
                    <div className="relative z-[1] flex h-40 w-full shrink-0 border-t border-zinc-700/80 bg-zinc-900 sm:h-auto sm:min-h-[8.5rem] sm:w-[min(42%,20rem)] sm:border-l sm:border-t-0">
                        <img
                            src={imageSrc}
                            alt=""
                            className="h-full w-full object-cover object-center transition-opacity duration-300"
                            loading="lazy"
                            onError={(e) => {
                                ;(e.target as HTMLImageElement).style.opacity = '0'
                            }}
                        />
                        <div
                            className="pointer-events-none absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/40 to-transparent sm:bg-gradient-to-l"
                            aria-hidden
                        />
                    </div>
                </div>
            </div>

            <div className="space-y-5">
                <div className="space-y-2">
                    <label className={fieldLabel}>Assigned sensor(s)</label>
                    <Select
                        instanceId={`sensor-group-${String(completedSystems.variable)}-sensors`}
                        inputId={`sensor-group-${String(completedSystems.variable)}-sensors-input`}
                        options={sensorOptions}
                        placeholder="Assign sensor(s)…"
                        styles={customStyles}
                        isMulti
                        isSearchable
                        closeMenuOnSelect={false}
                        hideSelectedOptions={false}
                        blurInputOnSelect={false}
                        captureMenuScroll={false}
                        menuShouldScrollIntoView
                        menuPlacement="auto"
                        value={assignedSensors}
                        onChange={(selected: unknown) => {
                            const list = (selected as { value: string; label: string }[] | null) ?? []
                            setAssignedSensors(list)
                            setMappedAssignedSensors(list)
                            const updatedSensorGroups = [...sensorGroups]
                            const existingGroupIndex = updatedSensorGroups.findIndex(
                                (group) => group.variable === completedSystems.variable
                            )

                            if (existingGroupIndex !== -1) {
                                updatedSensorGroups[existingGroupIndex].sensors = list.map((item) => item.value)
                            } else {
                                updatedSensorGroups.push({
                                    variable: completedSystems.variable,
                                    sensors: list.map((item) => item.value),
                                    controllers: [],
                                })
                            }
                            setSensorGroups(updatedSensorGroups)
                        }}
                    />
                </div>

                <div className="space-y-2">
                    <label className={fieldLabel}>Assigned controller(s)</label>
                    <Select
                        instanceId={`sensor-group-${String(completedSystems.variable)}-controllers`}
                        inputId={`sensor-group-${String(completedSystems.variable)}-controllers-input`}
                        options={controllerOptions}
                        placeholder="Assign controller(s)…"
                        styles={customStyles}
                        isMulti
                        isSearchable
                        closeMenuOnSelect={false}
                        hideSelectedOptions={false}
                        blurInputOnSelect={false}
                        captureMenuScroll={false}
                        menuShouldScrollIntoView
                        menuPlacement="auto"
                        value={assignedControllers}
                        onChange={(selected: unknown) => {
                            const list = (selected as { value: string; label: string }[] | null) ?? []
                            setAssignedControllers(list)
                            setMappedAssignedControllers(list)
                            const updatedSensorGroups = [...sensorGroups]
                            const existingGroupIndex = updatedSensorGroups.findIndex(
                                (group) => group.variable === completedSystems.variable
                            )

                            if (existingGroupIndex !== -1) {
                                updatedSensorGroups[existingGroupIndex].controllers = list.map((item) => item.value)
                            } else {
                                updatedSensorGroups.push({
                                    variable: completedSystems.variable,
                                    sensors: [],
                                    controllers: list.map((item) => item.value),
                                })
                            }

                            setSensorGroups(updatedSensorGroups)
                        }}
                    />
                </div>

                <div className="rounded-lg border border-zinc-800 bg-zinc-950/40 p-4">
                    <label className={`${fieldLabel} mb-3`}>Tolerances</label>
                    <RangeSlider
                        type={tolKey}
                        targetValue={program[tolKey]}
                        defaultValue={toleranceDefault}
                        advancedOptionsState={advancedOptions}
                        setAdvancedOptionsState={setAdvancedOptionsState}
                    />
                </div>
            </div>
        </div>
    )
}

export default SensorGroup
